/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type PropsWithChildren, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import type { AppConfig } from '@/types/config';
import CompanyLogo from '@/components/company-logo';
import ShipToBadge from './ship-to-badge';

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    mobileNavigation?: ReactNode;
    desktopNavigation?: ReactNode;
}

export default function Header({
    children,
    beforeHeader,
    mobileNavigation,
    desktopNavigation,
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const location = useLocation();
    const headerRef = useRef<HTMLElement>(null);
    const config = useConfig<AppConfig>();
    const showChat =
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight + 2}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    return (
        <header
            ref={headerRef}
            className="relative bg-header-background text-header-foreground w-full section-container">
            <div className="flex justify-end">{beforeHeader}</div>
            <div className="pt-2 lg:pt-4">
                {/* Top row: Logo left, Icons right */}
                <div className="flex flex-wrap items-center gap-x-1 lg:gap-x-6">
                    {mobileNavigation}

                    {/* Logo */}
                    <Link to="/" className="flex-1 lg:flex-0 flex items-center" data-testid="header-logo">
                        <CompanyLogo title={t('logoAlt')} className="h-5" />
                    </Link>

                    {/* Search - desktop only */}
                    <div className="hidden lg:block flex-1" data-testid="header-search-desktop">
                        <Search key={`${location.pathname}${location.search}`} />
                    </div>

                    {/* Icons group */}
                    <div className="flex items-center space-x-2">
                        <UITarget targetId="sfcc.header.before.cart" />
                        {showChat && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="cursor-pointer lg:px-4 px-1 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                                onClick={() => launchChat()}
                                aria-label={t('openChat')}>
                                <SparklesIcon />
                            </Button>
                        )}
                        <ShipToBadge />
                        <UserActions />
                        <WishlistIcon />
                        <CartBadge />
                    </div>

                    {children}
                </div>

                {desktopNavigation}

                {/* Mobile search - second row */}
                <div className="pb-4 lg:hidden" data-testid="header-search-mobile">
                    <Search key={`${location.pathname}${location.search}`} />
                </div>
            </div>
        </header>
    );
}
